import Illustration from "@/svg/illustration"
import { getImageUrl } from "@/utils/images"
import { DeleteIcon, EditIcon } from "@chakra-ui/icons"
import {
  Box,
  BoxProps,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  IconButton,
  VStack,
} from "@chakra-ui/react"
import { useTranslation } from "next-i18next"
import { memo, useEffect, useRef } from "react"
import Dropzone from "react-dropzone"
import { useFormContext } from "react-hook-form"

type FileFormats = "png" | "pdf" | "jpeg" | "jpg"

export const FileUpload = ({
  name,
  initialImageUrl,
  initialIsbnImageUrl,
  acceptedFileTypes = ["jpg", "png", "jpeg"],
  isDisabled,
  ...props
}: {
  name: string
  initialImageUrl?: string
  initialIsbnImageUrl?: string
  acceptedFileTypes?: FileFormats[]
  isDisabled?: boolean
} & BoxProps) => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const { setValue, watch, setError, formState } = useFormContext()
  const { errors } = formState
  const hasError = !!errors[name]?.message

  const fileFieldName = name + "File"
  const file = watch(fileFieldName)
  const imageId = watch(name)

  const handleFileUpload = (file: File) => {
    if (file && !isDisabled) {
      const fileFormat = file?.type?.split("/")[1] as FileFormats
      if (!acceptedFileTypes?.includes(fileFormat)) {
        setError(name, { message: t("upload.file_type_error") })
      } else {
        if (file.size > 1000000) {
          const image = new Image()
          image.src = URL.createObjectURL(file)
          image.onload = function () {
            const canvas = document.createElement("canvas")
            const ctx = canvas.getContext("2d")
            const ratio = image.width / image.height
            canvas.width = 800
            canvas.height = 800 / ratio
            ctx?.drawImage(image, 0, 0, canvas.width, canvas.height)
            ctx?.canvas.toBlob(
              (blob) => {
                const compressedFile = new File([blob as BlobPart], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                })
                setValue(fileFieldName, compressedFile)
              },
              file.type,
              1
            )
          }
        } else {
          setValue(fileFieldName, file)
        }
      }
    }
  }

  const handleUrlToFile = async (url: string) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()

      const extension = blob.type.split("/").pop() || "jpg"
      const fileName = `book_${Date.now()}.${extension}`

      const file = new File([blob], fileName, { type: blob.type })
      handleFileUpload(file)
    } catch (error) {
      setError(name, {
        message: t("upload.url_fetch_error") || "Failed to fetch image",
      })
    }
  }

  useEffect(() => {
    if (initialIsbnImageUrl) {
      handleUrlToFile(
        `/api/isbn/proxy?image=${encodeURIComponent(initialIsbnImageUrl)}`
      )
    }
  }, [initialIsbnImageUrl])

  if (Boolean(file) || (Boolean(initialImageUrl) && Boolean(imageId))) {
    const url = file ? URL.createObjectURL(file) : getImageUrl(initialImageUrl)
    return (
      <Box
        p={1}
        backgroundImage={url}
        height="325px"
        backgroundSize="contain"
        backgroundRepeat="no-repeat"
        {...props}
      >
        <IconButton
          icon={<DeleteIcon />}
          aria-label={t("global.delete")}
          borderRadius="50%"
          onClick={() => {
            setValue(name, null)
            setValue(fileFieldName, undefined)
          }}
          isDisabled={isDisabled}
        />
      </Box>
    )
  }

  return (
    <FormControl isInvalid={hasError}>
      <Dropzone
        onDrop={(acceptedFiles) => handleFileUpload(acceptedFiles[0])}
        disabled={isDisabled}
      >
        {({ getRootProps, getInputProps }) => (
          <VStack
            onClick={() => {
              if (!isDisabled) inputRef?.current?.click()
            }}
            cursor={isDisabled ? "not-allowed" : "pointer"}
            borderRadius="md"
            padding={2}
            {...getRootProps()}
            bg="#D4D4D4"
            boxShadow="lg"
            height="300px"
            justifyContent="space-between"
            paddingTop={12}
            margin="0 auto"
            {...props}
          >
            <Illustration width="200px" height="100px" />

            <Flex p="10px" justifyContent="flex-end" width="100%">
              <IconButton
                variant="brand"
                icon={<EditIcon />}
                aria-label={t("global.delete")}
                borderRadius="50%"
                onClick={() => inputRef?.current?.click()}
                isDisabled={isDisabled}
              />
            </Flex>

            <input
              type="file"
              onChange={(e) =>
                e?.target?.files && handleFileUpload(e?.target?.files[0])
              }
              ref={inputRef}
              style={{ display: "none" }}
              {...getInputProps()}
              disabled={isDisabled}
            />
          </VStack>
        )}
      </Dropzone>
      {!hasError && acceptedFileTypes && (
        <FormHelperText fontFamily="GTMaru">
          {t("upload.helpers_format", { format: acceptedFileTypes.join(", ") })}
        </FormHelperText>
      )}
      {hasError && <FormErrorMessage>{errors[name]?.message}</FormErrorMessage>}
    </FormControl>
  )
}

export default memo(FileUpload)
